import { defaultDiscount, defaultFieldsStyle, defaultProductCard, defaultShippingMethod, defaultTotalsStyle, } from '@cod/config-schema';
/** `Npx` from a numeric config value. Config stores unitless integers throughout. */
const px = (n) => `${n}px`;
/**
 * 🚨 Theme-CSS reset. Apply to every component that renders merchant-configured text.
 *
 * The legacy renderer painted into a **shadow root**, so the theme's stylesheet could not reach it —
 * `styles.ts` never had to defend against inherited typography. NubeSDK components are rendered by
 * the host directly into the page, and **that isolation is gone**. §4.11's "shadow-root isolation is
 * no longer our concern" is true only in the sense that we no longer *build* it; we also no longer
 * *have* it.
 *
 * Observed on a live Patagonia store 2026-08-05: the COD button rendered
 * `TEXT IN UPPERCASE WITH LETTER-SPACING` because the theme styles its own buttons that way, while
 * the admin preview — correctly — showed the merchant's text as typed. The merchant's config must
 * win over the theme, so anything a theme commonly sets on a button or label is stated explicitly
 * rather than left to inherit.
 *
 * Deliberately NOT resetting `fontFamily`: inheriting the store's typeface is what makes the form
 * look native to the theme, which is the whole argument for Option A over an iframe.
 *
 * ## Why `!important`
 *
 * A plain inline style already outranks any normal author rule, so the first version of this reset
 * omitted it — and **it did nothing on a live store**. Themes commonly write their button typography
 * as `text-transform: uppercase !important`, and an `!important` author rule beats a normal inline
 * style. The only way an inline declaration wins is to be `!important` itself.
 *
 * Keep the `!important` list short and confined to properties the merchant explicitly configures.
 * It is a blunt instrument: each one becomes something a theme cannot influence at all, which is
 * right for "show the merchant's text as typed" and wrong for anything meant to blend in.
 */
const THEME_RESET = {
    textTransform: 'none !important',
    letterSpacing: 'normal !important',
    textDecoration: 'none !important',
    lineHeight: '1.4',
    // `text()` renders as a paragraph-like element, and themes give paragraphs **asymmetric**
    // margins (`margin-top: 0; margin-bottom: 1em` is the common reset). Inside a centred flex row
    // the *margin box* is what gets centred, so an asymmetric margin shifts the text up and makes a
    // correctly-centred icon look low — the misalignment seen on a live store.
    //
    // ⚠️ This is why `...THEME_RESET` must be spread FIRST in every object below. It sets the
    // `margin` shorthand, which silently wipes any `marginTop`/`marginBottom` declared before it.
    // TypeScript cannot catch that (different keys), so ordering is the only guard.
    margin: '0',
};
/**
 * Border shorthand from a width/colour pair, or `none` at zero width — the same rule the CSS
 * used (`border_width > 0 ? … : 'none'`), applied consistently so a merchant who zeroes a width
 * gets no border rather than a 0px one that still paints on some renderers.
 */
/**
 * 🚨 There is no universal box-sizing reset any more, so EVERY sized object needs its own.
 *
 * The legacy stylesheet opened with `.cod-root, .cod-root * { box-sizing: border-box }` — one rule
 * covering everything it would ever render. That is not expressible here: the `style` prop reaches
 * a single component, and `styled()` rejects `*` outright (`Selector "*" is not supported`, thrown
 * inside the host's render, taking the component with it).
 *
 * So any object combining `width`/`maxWidth` with `padding` or `border` must say
 * `boxSizing: 'border-box'` itself. Miss one and it overflows its parent by exactly its padding
 * plus border — which is what made the Designer preview scroll sideways, every input sticking out
 * past the panel. `theme.test.ts` asserts the invariant across the whole object graph.
 */
const border = (width, color) => width > 0 ? `${px(width)} solid ${color}` : 'none';
/**
 * A merchant-chosen text alignment as the `justify-content` that positions it inside a flex row.
 *
 * The legacy stylesheet has the same map in `form-renderer/src/styles.ts` — the two stylesheets
 * are parallel by design (see this file's header), and the header must land in the same place on
 * both bundles while they are dual-shipped, so change them together.
 */
const JUSTIFY = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
};
/**
 * Field borders honour `border_mode` (`none` disables entirely) and `border_position`, which
 * draws a single edge — the underline style. Ported verbatim from `fieldBorderCss`, but as
 * discrete longhand properties: a `border: none; border-bottom: …` pair cannot be expressed in
 * one object key, so the reset is explicit per side.
 */
/**
 * All four sides, always — never the `border` shorthand.
 *
 * 🚨 **Mixing the shorthand with a side longhand in one style object breaks the admin preview on
 * re-render.** React applies a style object by diffing it against the previous one: a key that has
 * disappeared is cleared with `style.borderBottom = ''`, which removes the bottom longhands the
 * `border: none` shorthand had expanded into — so the side falls back to the UA default and paints
 * a dark border the merchant never asked for. Switching border position from `bottom` to `right`
 * left exactly that: the red right edge the merchant configured, plus a black bottom edge.
 *
 * Reported as "disabling the border leaves a black one", and only in the preview — the storefront
 * emits a whole stylesheet per render, so it has nothing to diff and never showed it.
 *
 * Keeping every side present on every render is what makes the diff a no-op. `edgeFor` is used for
 * the error variants too, so no object in this file reintroduces the mix; `theme.test.ts` asserts
 * the invariant across the whole graph.
 */
function edges(top, right, bottom, left) {
    return { borderTop: top, borderRight: right, borderBottom: bottom, borderLeft: left };
}
/** The same edge on all four sides — the shorthand's meaning, spelled as longhands. */
export function edgeAll(edge) {
    return edges(edge, edge, edge, edge);
}
/**
 * The input's `padding` with a caller-chosen left gutter — used when a static prefix has to be
 * cleared, since that width depends on the field rather than on the config.
 *
 * 🚨 Returns the whole `padding` SHORTHAND, never a `paddingLeft` longhand for the caller to layer
 * on top. One style object carrying both is the React-diffing trap that painted stray black
 * borders on fields (see `edges` above): a longhand that disappears between renders is cleared
 * with `style.paddingLeft = ''`, taking the shorthand's expansion with it.
 *
 * A standalone function rather than a member of `CodStyles`, deliberately — that object is "config
 * in, plain objects out", and a function on it is never equal to itself across two calls, which
 * `theme.test.ts`'s purity check rightly rejects.
 */
export function inputPaddingWithGutter(config, left) {
    const fs = config.fields_style ?? defaultFieldsStyle;
    const padY = px(10 + fs.sizing);
    return { padding: `${padY} 12px ${padY} ${left}` };
}
function fieldBorder(fs) {
    if (fs.border_mode === 'none')
        return edgeAll('none');
    const edge = `${px(fs.border_width)} solid ${fs.border_color}`;
    const on = (side) => fs.border_position === 'all' || fs.border_position === side ? edge : 'none';
    return edges(on('top'), on('right'), on('bottom'), on('left'));
}
/**
 * Serialise a style object to CSS declarations.
 *
 * Exists so the `styled()` channel (below) can be generated from the **same** objects the `style`
 * prop uses. Two hand-written copies of the button's look would drift, and "the storefront must
 * match the Designer exactly" is precisely the thing drift breaks.
 */
export function styleToCss(style, important = false) {
    return Object.entries(style)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => {
        const value = String(v);
        // Never double it up: some objects already carry `!important` in the value (THEME_RESET).
        const bang = important && !value.includes('!important') ? ' !important' : '';
        return `${k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}:${value}${bang}`;
    })
        .join(';');
}
/**
 * 🚨 The COD button's CSS, for the `styled()` channel rather than the `style` prop.
 *
 * **Why this component alone gets special treatment.** The open button is the one thing we render
 * that sits in the theme's most heavily styled territory — a storefront's button rules are its most
 * specific, most `!important`-laden CSS. Two rounds of fixes through the `style` prop failed on a
 * live Patagonia store: the theme kept forcing `text-transform: uppercase` and collapsing the icon
 * gap, and the merchant's configured label rendered in caps while the Designer showed it as typed.
 *
 * The `style` prop and `styled()` are **different channels**, not two ways to do one thing:
 *
 * | | `style` object | `styled()` |
 * | --- | --- | --- |
 * | Applied as | inline style | a real CSS rule the host injects |
 * | `!important` | unreliable — depends how the host applies it | parsed by the CSS parser, works |
 * | Pseudo-classes / `@media` | ❌ impossible | ✅ `&:hover`, `&:disabled`, `@media` |
 * | Descendant selectors | ❌ | ✅ via `&` |
 *
 * `& *` is what makes this hold: a theme rule may target the button, a span inside it, or both, and
 * we cannot know which per theme. Resetting the button and its descendants covers all of them.
 *
 * The declarations come from `codStyles()`, so the merchant's config stays the single source; only
 * the defensive overrides are added here.
 */
/**
 * 🚨 The host's `styled()` selector grammar is RESTRICTED. Two rules, both learned the hard way:
 *
 * 1. **No bare declarations at the top level.** A first version led with
 *    `styleToCss(openButton);&{…}` and the button vanished. Whether that is legal depends on
 *    whether the host injects the string raw or wraps it in a generated class, and we cannot see
 *    which — so every declaration goes inside a `&` block, which is valid either way.
 *
 * 2. **No `*`.** A second version used `&,& *{…}` to reach descendants and the host threw
 *    `Error: Selector "*" is not supported.` — inside its own render, taking the whole component
 *    with it. Our `try/catch` cannot help: the throw happens on the main thread, after the tree has
 *    left the worker.
 *
 * **Stick to `&` and `&:<pseudo>`.** Anything a descendant needs, give that descendant its own
 * `styled()` — see `THEME_RESET_CSS`, applied to the button and its label separately.
 */
const THEME_RESET_CSS = 'text-transform:none !important;letter-spacing:normal !important;text-decoration:none !important';
/** `.cod-field-error { color: #d33 }` — one constant, because `fieldErrorCss` restates it. */
const FIELD_ERROR_COLOR = '#d33';
/**
 * Theme defence for any component that renders merchant-configured text.
 *
 * Applied per-component precisely because `& *` is unavailable — each element that must resist the
 * theme carries its own rule.
 */
export const themeResetCss = () => `&{${THEME_RESET_CSS}}`;
/**
 * Per-input CSS for the two things the `style` prop cannot express.
 *
 * Both come straight from `styles.ts` and are visible in the Designer preview:
 *
 *     .cod-input::placeholder { color: <fields_style.placeholder_color> }
 *     .cod-input:focus        { border-color: <accent>; box-shadow: 0 0 0 2px … }
 *
 * ⚠️ **Whether these apply depends on where the host puts the generated class.** If it lands on the
 * `<input>`, both work. If it lands on the field's outer container, `::placeholder` and `:focus`
 * simply match nothing — harmless, and no worse than today. Unlike `*`, both are ordinary valid
 * selectors, so this should not trip the host's grammar check the way `&,& *` did.
 *
 * If the placeholders stay grey after this ships, that is the answer: the class is on the container,
 * and the colour needs a different route.
 */
export function codInputCss(config) {
    const fs = config.fields_style ?? defaultFieldsStyle;
    const accent = config.appearance.accent_color;
    return (`&::placeholder{color:${fs.placeholder_color}}` +
        `&:focus{border-color:${accent};box-shadow:0 0 0 2px ${accent}4d;outline:none}`);
}
/**
 * CSS for the input wrapper, via `styled()`.
 *
 * This is what lets the placeholder overlay disappear as the buyer types **without a rebuild**.
 *
 * The host's `field` renders its label BELOW the input and ignores `placeholder` (both confirmed on
 * a live store), so placeholder text inside the control has to be our own node. But hiding that
 * node when the field has content would need a repaint per keystroke — and a repaint tears the
 * focused input out of the DOM, which is what broke typing.
 *
 * So the overlay inherits its colour from this wrapper, and the wrapper turns that colour
 * transparent on `:focus-within`. Focus alone hides it — no state, no rebuild. The rebuild that
 * removes it for good happens on blur, when focus has already gone.
 *
 * Only `&` selectors: the host rejects anything broader (`Selector "*" is not supported`).
 */
/**
 * 🚨 The TEXTAREA's skin, split across the wrapper and the control inside it.
 *
 * **What the host actually does with `txtarea`, measured on a live SDK store:**
 *
 *  - `style.input` does **not** land. `field` and `txtarea` publish the same
 *    `{ container, label, input }` contract (`nube-sdk-types@0.90.0`) and the host honours it for
 *    `field` — every single-line input renders the merchant's background, border and 40px icon
 *    gutter — but on `txtarea` the control came out with the browser's own white background, no
 *    min-height and no left padding, so the buyer's first characters were typed under the icon.
 *  - `styled()` lands on the component's **wrapper**, not on the control. Putting the skin in an
 *    `&{…}` rule therefore drew a second frame: a correctly styled box with a plain white
 *    `<textarea>` sitting inside it.
 *
 * So the wrapper carries the field's frame — that is what puts the icon inside a real-looking
 * field — and a descendant rule dissolves the control's own chrome so only one box shows.
 *
 * 🚨 **`& textarea` is the one descendant selector we rely on anywhere.** The host rejects `*`
 * outright (`Selector "*" is not supported`, thrown inside its render, taking the component with
 * it), so a plain tag descendant is a deliberate, narrow bet. If the order-note field ever renders
 * blank, this is the first thing to suspect — drop back to `&{…}` alone and the field returns
 * (with the doubled frame).
 *
 * The legacy bundle needs none of this: `.cod-textarea.cod-has-icon` is a real stylesheet rule
 * inside a shadow root, and it measures 40px in Chrome.
 */
/**
 * The popup's × through `styled()`, because a bare `<button>` is in the theme's crosshairs.
 *
 * A storefront's button rules are its most specific and most `!important`-laden CSS — the open
 * button needed the same treatment (`codButtonCss`) after two rounds of trying the style prop. A
 * theme that gives every button a background, a border and a min-width turns this glyph into a
 * block in the panel's corner.
 *
 * `:hover` is the other reason: it cannot be expressed in a style object at all.
 */
/**
 * 🚨 The terms line's layout, on the `styled()` channel rather than the `style` prop.
 *
 * Reported from a live SDK store: with a T&C **URL** set, the checkbox sat at the far left of an
 * otherwise empty row, the link was squeezed into a narrow column against the RIGHT edge, wrapped
 * onto two lines, and could not be tapped — while the Designer preview, which paints the same tree
 * through Nimbus, looked correct.
 *
 * Two causes, and both are the `style` prop losing (§4.11):
 *
 * 1. **`consentRow`'s flex rules go through the style prop**, so they hold only where the theme
 *    states nothing about that element. The uppercasing in the report proves the theme was winning
 *    here: `styles.terms.link` carries `text-transform: none !important` inline and the storefront
 *    still rendered caps.
 * 2. 🚨 **The checkbox is handed `label: ''` when there is a URL** — the visible text has to be a
 *    sibling `link`, because the host's checkbox label is a plain string and cannot contain one.
 *    An empty label does NOT make the host's checkbox narrow: its container is a full-width form
 *    control, so as a flex item it claims the row and shrinks only as far as the link's longest
 *    word. That is exactly the empty left side and the two-line link, and a container that wide
 *    sitting over the text is why tapping the link hit the checkbox instead.
 *
 * The fix does not guess at the host's checkbox CSS, which we cannot see. The box is **pinned to
 * its own content** and a `box` — a primitive we own outright — is what grows beside it. So the row
 * is right whatever the host wraps a checkbox in.
 *
 * 🚨 The pin applies ONLY when the label is a link. With no URL the checkbox carries the text and
 * must be free to take the row and wrap; pinning it there would push a long consent sentence out
 * of its box.
 *
 * Grammar: `&` blocks only, no `*` — see `THEME_RESET_CSS`'s header for what the host rejects.
 */
export function consentRowCss() {
    return ('&{display:flex !important;align-items:flex-start !important;gap:10px !important;' +
        'width:100% !important;text-align:left !important}');
}
/** The checkbox, pinned to its own width so it can never claim the row. */
export function consentBoxCss() {
    return '&{flex:0 0 auto !important;width:auto !important;min-width:0 !important}';
}
/** The label column: everything the checkbox is not, and reading from the left. */
export function consentTextCss() {
    return '&{flex:1 1 auto !important;min-width:0 !important;text-align:left !important}';
}
/**
 * The terms link's typography, on the channel that beats a theme.
 *
 * `text-decoration: underline` rather than `THEME_RESET_CSS`'s `none`: a link that does not look
 * like one is not a link — the same exception `styles.terms.link` already makes inline.
 */
export function termsLinkCss(config) {
    const t = config.consent.terms.style;
    return (`&{text-transform:none !important;letter-spacing:normal !important;` +
        `text-decoration:underline !important;color:${t.link_color} !important;` +
        `font-size:${t.font_size}px !important;line-height:1.4}`);
}
export function codCloseCss(config) {
    return (`&{${THEME_RESET_CSS};background:none !important;border:none !important;` +
        `padding:0 !important;min-width:0 !important;width:auto !important;height:auto !important;` +
        `box-shadow:none !important;color:${config.appearance.text_color} !important;` +
        `font-size:24px !important;line-height:1 !important;opacity:.7;cursor:pointer !important}` +
        `&:hover{opacity:1}`);
}
export function codTextareaCss(config, control) {
    const fs = config.fields_style ?? defaultFieldsStyle;
    const accent = config.appearance.accent_color;
    // The frame moves to the wrapper; the control keeps only what it needs to fill it.
    const { minHeight, resize, ...frame } = control;
    return (
    // The frame keeps the min-height, and becomes a flex box so the control stretches to fill it.
    // `height: 100%` on the control would be circular here — the wrapper is sized by its content.
    `&{${styleToCss({ ...frame, minHeight: String(minHeight), display: 'flex' }, true)}}` +
        // Everything the browser draws for a bare `<textarea>` — frame, inset padding, focus ring —
        // has to go, or it shows through the wrapper's own.
        `& textarea{background:transparent !important;border:none !important;outline:none !important;` +
        `box-shadow:none !important;padding:0 !important;margin:0 !important;width:100% !important;` +
        // Fills the frame rather than sizing itself: `min-height: 0` is what lets a flex item shrink
        // to the frame instead of forcing it taller.
        `flex:1 1 auto !important;min-height:0 !important;font:inherit !important;` +
        `color:${fs.text_color} !important;resize:${String(resize ?? 'vertical')} !important;` +
        `display:block !important;box-sizing:border-box !important}` +
        `& textarea::placeholder{color:${fs.placeholder_color}}` +
        // The focus ring belongs to the frame the buyer sees, which is now the wrapper.
        `&:focus-within{border-color:${accent} !important;box-shadow:0 0 0 2px ${accent}4d !important}`);
}
export function inputWrapCss(config) {
    const fs = config.fields_style ?? defaultFieldsStyle;
    return placeholderCss(fs.placeholder_color);
}
/**
 * 🚨 The wrapper colours the overlay, and blanks it on focus. Inheritance is the ONLY channel here.
 *
 * `styled()` accepts nothing broader than `&`, so a rule on the wrapper cannot address the overlay
 * directly — it can only set an **inherited** property that the overlay does not override. `color`
 * is that property, and it is why the overlay declares `color: inherit`.
 *
 * A revision in between used a custom property (`--cod-ph`) toggling the overlay's opacity, on the
 * theory that the colour route had failed. It had not: the placeholder was rendering in the
 * merchant's configured colour all along, which happened to be red. Custom properties do NOT
 * survive to the overlay, so that version simply stopped hiding on focus — the buyer typed
 * underneath the placeholder until they blurred the field.
 *
 * What genuinely was broken then, and is fixed separately, is that the field ICONS inherited this
 * colour too (their SVG strokes are `currentColor`). `inputIcon` now carries an explicit colour,
 * which is what makes this channel safe to use for the overlay alone.
 */
const placeholderCss = (color) => `&{color:${color}}&:focus-within{color:transparent}`;
/**
 * The same trick for the discount input, which has its OWN placeholder colour in the Designer
 * (`.cod-discount .cod-input::placeholder { color: <discount.input_placeholder_color> }`, written
 * more specifically than the global rule precisely so it wins).
 */
export function discountWrapCss(config) {
    const ds = config.discount ?? defaultDiscount;
    return placeholderCss(ds.input_placeholder_color);
}
/**
 * `@keyframes` + the `animation` declaration for a merchant-chosen button animation, or `''` for
 * `none`. Ports the four `.cod-anim-*` classes and their keyframes verbatim.
 *
 * Emitted through `styled()`, which is the channel `nube-sdk-ui`'s own `keyframes()` helper uses —
 * so an `@keyframes` block in a component's CSS is a first-class feature of the library, not
 * something we are smuggling past the host. The names are FIXED rather than generated: the
 * library's `keyframes()` mints a random id per call, which would change the component's CSS (and
 * therefore its `__internalId`) on every render and remount the button each time.
 *
 * Callers apply this ONLY when the merchant chose an animation, so a config that wants none keeps a
 * plain `button` with no `styled()` wrapper at all.
 */
const ANIMATIONS = {
    pulse: {
        frames: '0%,100%{transform:scale(1)}50%{transform:scale(1.04)}',
        duration: '1.4s',
    },
    shake: {
        frames: '0%,100%{transform:translateX(0)}25%{transform:translateX(-4px)}75%{transform:translateX(4px)}',
        duration: '1.6s',
    },
    bounce: {
        frames: '0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}',
        duration: '1.4s',
    },
    tada: {
        frames: '0%,100%{transform:scale(1) rotate(0)}10%,20%{transform:scale(.95) rotate(-3deg)}' +
            '30%,50%,70%,90%{transform:scale(1.05) rotate(3deg)}' +
            '40%,60%,80%{transform:scale(1.05) rotate(-3deg)}',
        duration: '2s',
    },
};
export function buttonAnimationCss(animation) {
    const spec = ANIMATIONS[animation];
    if (!spec)
        return '';
    const name = `cod-${animation}`;
    return `@keyframes ${name}{${spec.frames}}&{animation:${name} ${spec.duration} ease-in-out infinite}`;
}
/** Every animation name with keyframes, so a test can assert full enum coverage. */
export const ANIMATION_NAMES = Object.keys(ANIMATIONS);
/**
 * The COD button's defensive CSS.
 *
 * **Only overrides — no appearance.** The button's colours, radius and padding come from the
 * `style` prop, which is known to render. If the host ever ignores `styled`, the button still looks
 * correct and merely inherits the theme's typography: a visible regression rather than a missing
 * button. Enhancement, not dependency.
 */
export function codButtonCss(_config) {
    return [
        // Typography the merchant configured must beat the theme's `!important` button rules.
        // `gap` is what separates the icon from the label; themes collapse it.
        `&{${THEME_RESET_CSS};display:inline-flex !important;align-items:center !important;` +
            'justify-content:center !important;gap:8px !important;' +
            // 🚨 GEOMETRY, not just typography — added after the label kept rendering ~8px above the
            // button's centre on a live store. A theme that pads its buttons asymmetrically (a taller
            // bottom for a shadow or an icon slot) pushes our content up, and the inline `padding` in
            // `openButton` loses to the theme's `!important`. Restating it here is the only channel
            // that outranks it.
            'padding:12px 18px !important;line-height:1 !important;' +
            'min-height:0 !important;height:auto !important}',
        // Expressible again through this channel, unlike the style prop — the legacy CSS had this.
        '&:hover{opacity:.9}',
    ].join('');
}
/**
 * A button LABEL's CSS, through `styled()` rather than the style prop.
 *
 * The colour is here as well as on the content box because a theme that colours its buttons' inner
 * text beats a plain inherited value — the merchant's label rendered in the theme's colour on top
 * of the merchant's background. The style prop is kept in place too; this is the layer that wins
 * when the theme fights, and it degrades to the style prop if the host ever ignores `styled`.
 */
export function buttonLabelCss(color, lineHeight = '1') {
    return (`&{${THEME_RESET_CSS};color:${color} !important;` +
        `margin:0 !important;line-height:${lineHeight} !important}`);
}
/**
 * The same reset for the SUBMIT and action (WhatsApp / link) button labels.
 *
 * 🚨 Measured on a live storefront: `topGap 17px / bottomGap 29px` inside a button with a symmetric
 * `padding: 16px`. The 12px difference was exactly the theme's `p { margin-bottom: 12px }` — the
 * label's *margin box* is what the flex container centres, so an asymmetric margin lifts the
 * visible text. `styles.buttonLabel` already sets `margin: '0 !important'`, but that is the STYLE
 * PROP, and inline `!important` does not beat a theme rule (`MIGRATION.md` §4.11). `styled()` is
 * the only channel that does, and it has to be on the label itself — `& *` is not a legal selector
 * and throws inside the host's render.
 *
 * Line-height 1.4 rather than the open button's 1: those labels have no icon to align against, and
 * collapsing the line box onto the em box pushes all-caps text (the submit button is uppercase)
 * above centre. The legacy leaves `.cod-action-text` to inherit `.cod-root`'s 1.4.
 */
export function formButtonLabelCss(color) {
    return buttonLabelCss(color, '1.4');
}
/**
 * The `link` nested inside a WhatsApp button that also creates an order.
 *
 * 🚨 That nesting is not decoration — it is the only way one tap can both navigate and reach a
 * handler; see `whatsappNode`. The anchor therefore has to BE the button's whole surface, or a tap
 * on the padding would create an order and go nowhere. So the outer button drops its padding and
 * this carries it.
 *
 * Through `styled()` because a storefront theme always has rules for `a` — colour and
 * `text-decoration` at minimum — and the style prop only wins where the theme is silent
 * (`MIGRATION.md` §4.11).
 */
export function actionLinkFillCss(color) {
    return (`&{display:flex !important;width:100% !important;box-sizing:border-box !important;` +
        `flex-direction:column;align-items:center;justify-content:center;gap:2px;` +
        `color:${color} !important;text-decoration:none !important}`);
}
/**
 * The subtitle line under a submit/action label. Same theme-margin exposure as the label — it only
 * escaped notice because a subtitle is optional and its absence is what made the bug visible.
 */
export function buttonSubtitleCss(color) {
    return (`&{${THEME_RESET_CSS};color:${color} !important;` +
        'margin:0 !important;line-height:1.4 !important}');
}
/**
 * The inline error under a field, through the channel that outranks the theme.
 *
 * ⚠️ **Hardening, not the fix for the reported gap** — that was `nativeContainer`, the theme's
 * `.form-group` margin on the host's wrapper. This exists because `text()` renders as a paragraph
 * and themes margin paragraphs: the style prop lands inline and normally wins, but a theme that
 * writes `p{margin:… !important}` (they do exactly that for buttons — see `discountApplyCss`)
 * would beat it, and `styled()` is the channel that holds either way.
 *
 * Same shape as `buttonSubtitleCss`. Colour and size are restated for the same reason.
 */
export function fieldErrorCss() {
    return (`&{${THEME_RESET_CSS};margin:3px 0 0 !important;` +
        `color:${FIELD_ERROR_COLOR} !important;font-size:12px !important;line-height:1.4 !important}`);
}
/**
 * The form-level message (a failed order, the consent warning) inside its coloured box.
 *
 * Identical exposure to `fieldErrorCss`, and identical status: hardening. The text is a paragraph,
 * so a theme margin it wins would land INSIDE the box and pad it asymmetrically. The colour stays
 * on the style prop — it differs per message kind, and the box makes it legible either way.
 */
export function messageTextCss() {
    return `&{${THEME_RESET_CSS};margin:0 !important;line-height:1.4 !important}`;
}
/**
 * The height the discount input and its Apply button BOTH take, so the pair cannot disagree.
 *
 * Neutralising the button's padding and `min-height` inline was not enough — it stayed half again
 * as tall as the input across three attempts, which means the height is decided by something we
 * cannot address property-by-property. Pinning both controls to one computed value makes them
 * match by construction rather than by both happening to resolve the same way.
 *
 * `20px` is the line box of 15px text; the rest is the input's own padding and border, so the pair
 * lands at the same height as every other field in the form.
 */
export function discountControlHeight(config) {
    const fs = config.fields_style ?? defaultFieldsStyle;
    const ds = config.discount ?? defaultDiscount;
    return px(2 * (10 + fs.sizing) + 20 + 2 * ds.input_border_width);
}
/** The Apply button's geometry overrides, for the channel that outranks the theme. */
export function discountApplyCss(config) {
    const height = discountControlHeight(config);
    return (`&{padding:0 14px !important;line-height:1 !important;` +
        `min-height:${height} !important;height:${height} !important;` +
        'display:inline-flex !important;align-items:center !important;justify-content:center !important}');
}
/** Build every style object the form tree needs from one config document. */
export function codStyles(config) {
    const a = config.appearance;
    const b = config.button;
    const fs = config.fields_style ?? defaultFieldsStyle;
    const pc = config.product_card ?? defaultProductCard;
    const ts = config.totals_style ?? defaultTotalsStyle;
    const ds = config.discount ?? defaultDiscount;
    const sm = config.shipping_method ?? defaultShippingMethod;
    // `sizing` widens inputs by padding rather than a height, so a merchant's choice survives
    // whatever line-height the host theme applies. Same 10px base as the CSS.
    const fieldPadY = px(10 + fs.sizing);
    const fieldShadow = fs.shadow > 0 ? `0 ${px(Math.round(fs.shadow / 4))} ${px(fs.shadow)} rgba(0,0,0,.12)` : 'none';
    const fieldInput = {
        // 🚨 See BORDER_BOX below — without it `width: 100%` plus padding overflows the field's own
        // wrapper, and the flex chain above cannot shrink it back.
        boxSizing: 'border-box',
        // `.cod-input, .cod-textarea { font-family: inherit }` — and the legacy spelled out why: form
        // controls do NOT inherit the font by default, so a bare `<textarea>` renders in the UA's
        // monospace while the text inputs beside it do not.
        fontFamily: 'inherit',
        backgroundColor: fs.bg_color,
        color: fs.text_color,
        borderRadius: px(fs.border_radius),
        boxShadow: fieldShadow,
        padding: `${fieldPadY} 12px`,
        fontSize: '15px',
        width: '100%',
        ...fieldBorder(fs),
    };
    /**
     * The `date` control: an input's skin on a button's element.
     *
     * Built from `fieldInput` rather than hand-written, so it inherits the merchant's background,
     * border mode/position/width, radius, shadow and `sizing` automatically. A separate object would
     * mean every field-style setting had to be applied twice and would drift the first time one was
     * added — the exact failure the shared `codStyles` exists to prevent.
     *
     * `THEME_RESET` first (it sets the `margin` shorthand — see its note), then the input skin, then
     * the three things a button needs and an input does not: a left-aligned flex row for the
     * value/glyph pair, and a pointer cursor.
     */
    const dateTrigger = {
        ...THEME_RESET,
        ...fieldInput,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        textAlign: 'left',
        cursor: 'pointer',
    };
    /** The calendar's month/year dropdowns. Deliberately smaller than a form field — they are chrome. */
    const calendarSelect = {
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        fontSize: '14px',
        minWidth: '0',
        padding: '6px 8px',
        color: fs.text_color,
        backgroundColor: fs.bg_color,
        borderRadius: px(Math.min(fs.border_radius, 8)),
        ...edgeAll('1px solid rgba(0,0,0,.14)'),
        cursor: 'pointer',
    };
    /** Same as `nativeLabelHidden`: passed for accessibility, never painted. */
    const hiddenLabel = { display: 'none' };
    /** The `nativeContainer` reset, for the style groups built before the object below. */
    const nativeContainerStyle = { width: '100%', margin: '0' };
    // `hide_labels` suppresses the caption above every input. The placeholder (which falls back to
    // the label) still identifies the field, so this is display-only — the label text must still be
    // passed to the component for accessibility.
    //
    // `marginBottom` assumes the label sits ABOVE the input, which the component reference states
    // explicitly ("the label text displayed above the field"). The Phase 2 spike appeared to show it
    // rendering BELOW on a styled `field` while `select` kept it above — most likely our `container`
    // / `input` overrides displacing a host-positioned label rather than the documented order being
    // wrong. If Phase 5 confirms it renders below, this becomes `marginTop` and the per-part style
    // overrides need re-checking, because that would mean they can disturb the component's own
    // layout and not just its appearance.
    const labelBase = fs.hide_labels
        ? { display: 'none' }
        : {
            // Themes routinely uppercase form labels; THEME_RESET shows the merchant's text as typed.
            ...THEME_RESET,
            color: a.text_color,
            // `.cod-label { font-size: 14px; font-weight: 500; margin-bottom: 6px }` — the first port
            // used 13px/600/4px, so every label was slightly smaller and tighter than the Designer.
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
        };
    /**
     * `.cod-form` — shared by the modal and the embedded/preview panel.
     *
     * `padding: 0 20px 20px` and nothing on top: the HEADER supplies the top spacing
     * (`.cod-header { padding: 16px 0 }`). With the header hidden the form would butt against its
     * own edge, so `.cod-no-header` restores 24px. The preview had `padding: 0` outright, which is
     * why it rendered edge to edge.
     */
    const panelBase = {
        position: 'relative',
        // 🚨 An explicit COLUMN, because `box` defaults to a flex ROW on the host.
        //
        // The panel holds one child — the body column — and as a flex item in a row that child sizes to
        // its own content rather than filling the panel. Inside a 460px modal the two are nearly the
        // same width so nothing showed; embedded on a full-width product page the panel stretched to
        // ~830px while its content stayed at ~410px, and the form looked twice as wide as its contents.
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: a.bg_color,
        color: a.text_color,
        borderRadius: px(a.border_radius),
        padding: config.header.enabled ? '0 20px 20px' : '24px 20px 20px',
        // 🚨 `:host { all: initial }` reset the legacy form's font-size to the initial value — 16px —
        // and `.cod-root` set only the line-height. So EVERY caption the stylesheet left unsized
        // (`.cod-shipping-name`, `.cod-shipping-price`, the consent lines) renders at 16px in the
        // Designer preview. There is no `all: initial` here: without this, those same captions
        // inherit the storefront theme's body size and the form is uniformly a size out.
        fontSize: '16px',
        lineHeight: '1.4',
        width: '100%',
        // `maxWidth` as well as `width`: in a shrink-to-fit container (a flex item with the default
        // `min-width: auto`) a percentage width resolves against the content and the panel grows to
        // max-content, taking the whole form with it. That is a horizontal scrollbar in the preview.
        maxWidth: '100%',
        // 🚨 Long words BREAK rather than widening whatever holds the form.
        //
        // `overflow-wrap` is an inherited property, so one declaration here reaches every caption in
        // the tree — which is the only way to say it globally: `styled()`'s selector grammar is `&`
        // and `&:<pseudo>` only, so there is no descendant rule to write. `anywhere` rather than
        // `break-word` on purpose: only `anywhere` also shrinks a box's **min-content** width, and
        // min-content is exactly what a shrink-to-fit ancestor measures. With `break-word` the text
        // would still wrap and the container would still be sized for the unbroken word.
        //
        // Nothing that must stay on one line is affected — `white-space: nowrap` disables wrapping
        // outright, so the prices, the savings plaque and the "Best deal" pill are untouched.
        overflowWrap: 'anywhere',
        boxSizing: 'border-box',
    };
    return {
        panel: {
            ...panelBase,
            // 🚨 A DEFINITE width, because the host's dialog has none of its own.
            //
            // `modal_content` renders into a dialog the host sizes to FIT ITS CONTENT, so `width: 100%`
            // there is circular and resolves to shrink-to-fit: the form came out as wide as its widest
            // line. Retitling a quantity offer, or typing more into a "Title or text" block, changed the
            // width of the entire form — and a long enough caption pushed it past the cards, which is why
            // the prices and the plaque ended up painted over the tier title.
            //
            // 460px is not a new number: the legacy bundle pins `.cod-modal { width:100%; max-width:460px }`
            // inside its own overlay, and dual-ship means the two paths must measure the same. `maxWidth`
            // keeps it inside a narrow viewport, where the dialog is the smaller of the two.
            width: '460px',
            maxWidth: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
            // Headroom for the floating × when the merchant has hidden the Header field. `panelBase`
            // gives a header-less panel 24px, which the close button would sit on top of — the legacy
            // stylesheet reserves the same 48px (`.cod-form.cod-no-header:not(.cod-embedded)`). AFTER
            // the `padding` shorthand in `panelBase`, or it would be wiped by it.
            ...(config.header.enabled ? {} : { paddingTop: '48px' }),
            // 🚨 The host's dialog does NOT scroll our content — a full form ran off the bottom of the
            // screen on a live store with the submit button unreachable. The legacy overlay carried
            // `overflow-y: auto` itself (`.cod-overlay`); the equivalent has to live on the panel now.
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,.15)',
        },
        // `.cod-form.cod-embedded { box-shadow: 0 1px 6px rgba(0,0,0,.08); margin-top: 16px }`
        panelEmbedded: {
            ...panelBase,
            boxShadow: '0 1px 6px rgba(0,0,0,.08)',
            marginTop: '16px',
        },
        openButton: {
            ...THEME_RESET,
            boxSizing: 'border-box',
            // The button renders into a THEME slot, not into the panel, so it inherits nothing from
            // `panelBase` — it needs its own copy or a single long word in the caption stretches the
            // theme's buy-button column. Same reasoning as the offer containers in `offers.theme.ts`.
            overflowWrap: 'anywhere',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: b.bg_color,
            color: b.text_color,
            borderRadius: px(b.border_radius),
            border: border(b.border_width, b.border_color),
            padding: '12px 18px',
            marginTop: '10px',
            width: '100%',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            // NOTE: the CSS had `:hover { opacity: .9 }` and a 150ms transition. Neither survives into
            // an inline style object. Dropped rather than faked — NubeSDK's own `button` supplies a
            // native hover affordance, and a JS-driven one would need state for a purely cosmetic effect.
        },
        buttonContent: {
            ...THEME_RESET,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            // AFTER the spread: THEME_RESET sets lineHeight 1.4, which is right for body copy and wrong
            // inside a flex row — the taller line box pushes the text off-centre from the icon. tsc
            // caught the original ordering as TS2783 (specified more than once).
            lineHeight: '1',
        },
        // The label INSIDE the button. Typography only, never layout — applying `buttonContent` here
        // made the text node its own flex container with its own gap, which is what knocked it out of
        // alignment with the icon on a live store.
        buttonLabel: {
            ...THEME_RESET,
            // 🚨 NO `lineHeight: 1` here — THEME_RESET's 1.4 is what the legacy uses and it is correct.
            //
            // `.cod-action-text` carries no line-height rule at all, so it inherits `.cod-root`'s 1.4;
            // that is true for the submit, WhatsApp and open buttons alike. Forcing 1 made the line box
            // equal the em box, and for ALL-CAPS labels (the submit button is `text-transform: uppercase`)
            // the unused descender space then sits below the caps and pushes the visible text ABOVE
            // centre. It only looked right when a subtitle happened to fill the space underneath —
            // which is exactly how it was reported: centred with a subtitle, high without one.
            //
            // The row container (`buttonContent`) keeps its `lineHeight: 1` — that one is about the icon
            // sitting beside the label, and the label's own box still centres against it.
            // `!important` on top of THEME_RESET's plain `margin: 0`. A label inside a button renders
            // paragraph-like, and a theme that writes `p { margin: … !important }` beats a plain inline
            // style — adding ~1em above and below and inflating the button. Nothing else should be
            // setting a margin on a button's own label, so this is safe to force.
            margin: '0 !important',
        },
        header: {
            // `.cod-header { display:flex; align-items:center; justify-content:<align>;
            //  padding:16px 0; margin-bottom:18px; border-bottom:1px solid rgba(0,0,0,.12) }` — the
            // first port kept only a 12px margin, so the header read as a stray line of text with no
            // rule under it and merchants reported it as missing.
            //
            // The band holds exactly ONE child (the title, or a row of icon + title), so `justifyContent`
            // is what places it — this was `space-between`, which for a single child is `flex-start` and
            // is why the header could only ever be left-aligned. `textAlign` is set as well and is not
            // redundant: it is what aligns the second line once a long title wraps, since a wrapped flex
            // item fills the band and `justifyContent` then has no slack left to distribute.
            container: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: JUSTIFY[config.header.align],
                textAlign: config.header.align,
                width: '100%',
                boxSizing: 'border-box',
                padding: '16px 0',
                marginBottom: '18px',
                borderBottom: '1px solid rgba(0,0,0,.12)',
            },
            // `.cod-title { font-size:20px; font-weight:800; text-transform:uppercase;
            //  letter-spacing:.3px }`. Uppercase is the DESIGN here, like `.cod-submit` — so it is
            // re-applied after THEME_RESET, which would otherwise force the merchant's text as typed.
            title: {
                ...THEME_RESET,
                color: a.text_color,
                fontSize: '20px',
                fontWeight: '800',
                textTransform: 'uppercase !important',
                letterSpacing: '.3px !important',
            },
        },
        /**
         * `.cod-close { position:absolute; top:14px; right:16px; z-index:3; font-size:24px; opacity:.7 }`
         *
         * Form CHROME, not part of the header — so it survives the merchant hiding the Header field,
         * exactly as on the legacy path. `panelBase` is `position: relative`, which is what it anchors
         * to; the modal panel also reserves 48px of headroom when there is no header, or this would
         * float over the first block.
         */
        close: {
            ...THEME_RESET,
            position: 'absolute',
            top: '14px',
            right: '16px',
            zIndex: '3',
            backgroundColor: 'transparent',
            border: 'none',
            padding: '0',
            cursor: 'pointer',
            fontSize: '24px',
            lineHeight: '1',
            color: a.text_color,
            opacity: '.7',
        },
        field: {
            container: { marginBottom: '14px', width: '100%' },
            label: labelBase,
            input: fieldInput,
        },
        // Error state replaces the input object wholesale. The CSS used `.cod-input.cod-invalid` to
        // override; with no cascade the consumer picks one object or the other.
        // Longhands, not the `border` shorthand — see `edges` above: one object must never carry both.
        fieldInputError: { ...fieldInput, ...edgeAll('1px solid #d92d20') },
        fieldError: { ...THEME_RESET, color: FIELD_ERROR_COLOR, fontSize: '12px', marginTop: '3px' },
        nativeLabelHidden: { display: 'none' },
        // 🚨 The theme's `.form-group`, cancelled — read the field's header above before changing it.
        nativeContainer: nativeContainerStyle,
        placeholder: {
            position: 'absolute',
            left: '12px',
            // `inherit`, and deliberately: the wrapper's `styled()` rule sets this to the merchant's
            // placeholder colour and to `transparent` on `:focus-within`, which is how the text clears
            // the moment the buyer focuses the field — with no rebuild, and therefore without tearing
            // the focused input out of the DOM. See `inputWrapCss`.
            color: 'inherit',
            // 🚨 Anchored to the INPUT'S TEXT LINE, not centred on the wrapper.
            //
            // Two schemes were tried first — `top: 50%` + `translateY(-50%)`, then `top: 0; bottom: 0`
            // with flex centring — and both centre on the **wrapper**, which is the field component's
            // container, not the input. The host's container is taller than the input (it reserves room
            // below it for the label it renders there), so anything centred on it sits a few pixels low
            // inside the input. That is the "not quite centred" the merchant kept seeing.
            //
            // `top: <field padding>` lands exactly where the input's own text starts, because the input
            // is the wrapper's first in-flow child and carries that padding. It depends on nothing we
            // cannot see.
            top: fieldPadY,
            bottom: 'auto',
            fontSize: '15px',
            // So a tap lands on the input underneath rather than on the text.
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            margin: '0',
            // `normal`, matching the input's own text line box. `1` clips the line to exactly the font
            // size, and because a font's glyphs are not centred in that box the overlay sat a pixel or
            // two below where the typed text appears.
            lineHeight: 'normal',
        },
        placeholderWithIcon: { left: '40px' },
        // `.cod-textarea { min-height: 70px; resize: vertical }`
        // `margin: 0` because the adapter gives the `<textarea>` the theme's `m-top-quarter`
        // class alongside our inline style — same family of borrowed theme classes as
        // `form-group`, and the same remedy.
        textarea: { minHeight: '70px', resize: 'vertical', margin: '0' },
        placeholderLabel: { ...THEME_RESET, color: fs.placeholder_color, fontSize: '15px' },
        discountPlaceholder: { color: ds.input_placeholder_color },
        // `.cod-prefix { position: absolute; left: 12px; font-size: 15px; color: rgba(0,0,0,.55);
        //  pointer-events: none }`
        prefix: {
            position: 'absolute',
            left: '12px',
            // Same anchor as the placeholder it sits beside — without an explicit `top` this fell back
            // to its static position, which is the top of the wrapper rather than the input's text.
            top: fieldPadY,
            display: 'flex',
            fontSize: '15px',
            color: 'rgba(0,0,0,.55)',
            pointerEvents: 'none',
            margin: '0',
        },
        taxesMessage: { ...THEME_RESET, fontSize: '12px', opacity: '.7', marginTop: '6px' },
        text: { ...THEME_RESET },
        required: { ...THEME_RESET, color: '#d33', marginLeft: '3px' },
        // `.cod-checkbox { display: flex; align-items: flex-start; gap: 10px; font-size: 14px }`
        consentRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', width: '100%' },
        // The degraded fallback for `consentTextCss`; the `styled()` rule is what actually lands.
        consentText: { flex: '1 1 auto', minWidth: '0', textAlign: 'left' },
        radioGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
        /**
         * `.cod-radio-option { display: flex; align-items: center; gap: 8px; font-size: 14px;
         *  cursor: pointer }` — a BUTTON here, because the row has to be clickable and only `button`
         * and `link` take an `onClick` in NubeSDK. Transparent and unpadded so it reads as a row, not
         * as a button.
         */
        radioOption: {
            ...THEME_RESET,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            padding: '0',
            border: 'none',
            backgroundColor: 'transparent',
            color: a.text_color,
            fontSize: '14px',
            textAlign: 'left',
            cursor: 'pointer',
            boxSizing: 'border-box',
        },
        radioOptionLabel: { ...THEME_RESET, color: a.text_color, fontSize: '14px' },
        // Same marker the shipping rates draw — one visual language for "pick exactly one".
        radio: {
            width: '18px',
            height: '18px',
            borderRadius: '999px',
            border: `2px solid ${a.accent_color}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxSizing: 'border-box',
        },
        radioOn: {
            width: '18px',
            height: '18px',
            borderRadius: '999px',
            border: `2px solid ${a.accent_color}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxSizing: 'border-box',
        },
        radioDot: {
            width: '9px',
            height: '9px',
            borderRadius: '999px',
            backgroundColor: a.accent_color,
        },
        // `.cod-custom-text { margin: 0 0 14px; white-space: pre-wrap; word-break: break-word }`
        /**
         * 🚨 The reset is what stops a storefront theme crushing this block.
         *
         * `text()` renders paragraph-like, and themes size paragraphs in PIXELS
         * (`p { line-height: 20px }`). Stating no line-height of our own handed that rule the element
         * outright — measured in Chrome, a 51px "Title or text" came out with 20px line boxes, so
         * eleven lines of 51px glyphs stacked into 220px and overlapped each other. With the reset the
         * same block measures 71.4px lines. The merchant sees it correctly in the Designer preview
         * either way, because the admin has no such rule — which is why it reads as a storefront-only
         * bug.
         *
         * UNITLESS on purpose: `1.4` is a multiple of whatever font size the merchant picks, so one
         * value serves 12px body copy and a 51px heading. A pixel line-height is exactly the mistake
         * the theme is making. And 1.4 rather than 1 because the legacy bundle renders this block at
         * 1.4 (`.cod-root`), and dual-ship means the two paths must measure the same.
         */
        customText: {
            ...THEME_RESET,
            margin: '0 0 14px',
            whiteSpace: 'pre-wrap',
            // Stated here, not merely inherited from the panel: a theme rule on `p` beats inheritance,
            // and it is this element's min-content width that a shrink-to-fit slot measures the form by.
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
        },
        customImageWrap: { display: 'flex', justifyContent: 'center', margin: '0 0 14px' },
        customImage: { display: 'block', height: 'auto', borderRadius: '6px', maxWidth: '100%' },
        // `.cod-action-btn { display:flex; flex-direction:column; align-items:center; gap:2px;
        //  width:100%; padding:14px 18px; margin:0 0 14px; font-weight:700; line-height:1.25 }`
        actionButton: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            width: '100%',
            boxSizing: 'border-box',
            padding: '14px 18px',
            margin: '0 0 14px',
            fontWeight: '700',
            lineHeight: '1.25',
            textAlign: 'center',
            cursor: 'pointer',
            textDecoration: 'none !important',
        },
        // Ported verbatim from `.cod-input-wrap` / `.cod-icon`. The icon overlays the input's left
        // padding rather than sitting beside it — rendering it as a flex sibling (the first attempt)
        // shrank the input and pushed the glyph outside the field frame entirely.
        inputWrap: { position: 'relative', display: 'flex', alignItems: 'center', width: '100%' },
        inputIcon: {
            position: 'absolute',
            left: '12px',
            // Same anchor as `placeholder` — the input's text line — so the glyph and the placeholder
            // cannot disagree. The extra pixel centres the 16px glyph against the ~18px line box of
            // 15px text at `line-height: normal`.
            top: px(10 + fs.sizing + 1),
            bottom: 'auto',
            display: 'flex',
            // 🚨 EXPLICIT, never inherited. The glyphs stroke in `currentColor`, and the wrapper's colour
            // is the placeholder colour (see `inputWrapCss`) — so without this every field icon rendered
            // in the placeholder colour instead of `.cod-root { color: var(--cod-text) }` at 75%.
            color: a.text_color,
            opacity: '0.75',
            // So a tap on the glyph focuses the input beneath it instead of doing nothing.
            pointerEvents: 'none',
            zIndex: '1',
        },
        // A textarea is multi-line, and both already anchor to the first line — these stay as explicit
        // overrides so the intent survives if the default ever goes back to centring.
        inputIconTop: { top: px(10 + fs.sizing + 1), bottom: 'auto' },
        /** Placeholder pinned to the first line of a textarea. */
        placeholderTop: { top: fieldPadY, bottom: 'auto' },
        fieldInputWithIcon: { ...fieldInput, padding: `${fieldPadY} 12px ${fieldPadY} 40px` },
        fieldInputErrorWithIcon: {
            ...fieldInput,
            ...edgeAll('1px solid #d92d20'),
            padding: `${fieldPadY} 12px ${fieldPadY} 40px`,
        },
        datePicker: {
            // Every one of these starts from `fieldInput`, so a merchant's field background, border mode,
            // radius, shadow and sizing reach the date control unchanged — it has to sit in a column of
            // inputs and read as one of them, not as a button that wandered in.
            trigger: dateTrigger,
            triggerWithIcon: { ...dateTrigger, padding: `${fieldPadY} 12px ${fieldPadY} 40px` },
            triggerError: { ...dateTrigger, ...edgeAll('1px solid #d92d20') },
            triggerErrorWithIcon: {
                ...dateTrigger,
                ...edgeAll('1px solid #d92d20'),
                padding: `${fieldPadY} 12px ${fieldPadY} 40px`,
            },
            // `fs.text_color`, matching what a typed value in the field beside it renders as.
            value: { ...THEME_RESET, color: fs.text_color, fontSize: '15px' },
            // 🚨 The placeholder colour is set DIRECTLY here, not inherited from `inputWrapCss`.
            //
            // The text-field placeholder is an overlay that clears on `:focus-within`, which is why it
            // takes its colour from the wrapper (see `placeholderCss`). This one is a child of the
            // control itself and must never go transparent — the buyer taps the control to open the
            // calendar, and a placeholder that vanished on focus would leave an empty box.
            placeholder: { ...THEME_RESET, color: fs.placeholder_color, fontSize: '15px' },
            glyph: {
                display: 'flex',
                // Never shrink below the glyph: a long placeholder must ellipsise instead.
                flexShrink: 0,
                color: fs.text_color,
                opacity: '0.75',
            },
            panel: {
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                width: '100%',
                // 🚨 Rendered INLINE, under the control — not floated over the form.
                //
                // An absolutely positioned popup would need a measured anchor, and a worker has nothing to
                // measure with; it would also be clipped or scroll-trapped by the panel's own
                // `overflow-y: auto` (see `panel` above). Inline grows the form instead, which the modal
                // already scrolls, and is the one layout both bundles can reproduce identically.
                marginTop: '6px',
                padding: '10px',
                backgroundColor: a.bg_color,
                color: a.text_color,
                borderRadius: px(fs.border_radius),
                ...edgeAll(`1px solid ${a.accent_color}`),
                boxShadow: '0 6px 20px rgba(0,0,0,.12)',
            },
            head: { display: 'flex', alignItems: 'center', gap: '6px', width: '100%' },
            nav: {
                ...THEME_RESET,
                boxSizing: 'border-box',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: '32px',
                height: '32px',
                padding: '0',
                border: 'none',
                backgroundColor: 'transparent',
                color: a.text_color,
                borderRadius: '999px',
                cursor: 'pointer',
            },
            monthSelect: {
                container: nativeContainerStyle,
                label: hiddenLabel,
                select: { ...calendarSelect, flexGrow: 1 },
            },
            // Narrower than the month, since four digits need less room than "Septiembre".
            yearSelect: {
                container: nativeContainerStyle,
                label: hiddenLabel,
                select: { ...calendarSelect, flexShrink: 0 },
            },
            weekdayRow: { display: 'flex', width: '100%', gap: '2px' },
            weekday: {
                ...THEME_RESET,
                boxSizing: 'border-box',
                // `1 1 0` with `minWidth: 0`, so all seven columns are equal regardless of caption width —
                // `1 1 auto` sizes each to its own text and the grid stops lining up with the days below.
                flex: '1 1 0',
                minWidth: '0',
                padding: '4px 0',
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: '600',
                color: a.text_color,
                opacity: '0.6',
            },
            week: { display: 'flex', width: '100%', gap: '2px' },
            day: {
                ...THEME_RESET,
                boxSizing: 'border-box',
                flex: '1 1 0',
                minWidth: '0',
                // A 36px-tall tap target: the WCAG-ish floor for a thumb on a phone, which is where COD
                // buyers overwhelmingly are.
                height: '36px',
                padding: '0',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                backgroundColor: 'transparent',
                color: a.text_color,
                borderRadius: px(Math.min(fs.border_radius, 8)),
                fontSize: '14px',
                cursor: 'pointer',
            },
            daySelected: {
                backgroundColor: a.accent_color,
                // White on the accent, like every other selected surface in the form. The accent is a
                // merchant colour, so this is the one pairing that is legible whatever they picked.
                color: '#ffffff',
                fontWeight: '700',
            },
            // An OUTLINE, never a fill: today and the selected day can be the same cell, and two fills
            // would fight. The outline simply disappears under the selected background.
            dayToday: { ...edgeAll(`1px solid ${a.accent_color}`) },
            blank: { boxSizing: 'border-box', flex: '1 1 0', minWidth: '0', height: '36px' },
            foot: { display: 'flex', justifyContent: 'flex-end', width: '100%' },
            clear: {
                ...THEME_RESET,
                boxSizing: 'border-box',
                padding: '4px 6px',
                border: 'none',
                backgroundColor: 'transparent',
                color: a.text_color,
                opacity: '0.7',
                fontSize: '13px',
                cursor: 'pointer',
            },
        },
        select: {
            container: nativeContainerStyle,
            label: labelBase,
            select: fieldInput,
        },
        checkbox: {
            container: { marginBottom: '8px' },
            label: { ...THEME_RESET, color: a.text_color, fontSize: '14px' },
            // The SELECTION colour, same as the radios below and the shipping-method dots. This was
            // `primary_color`, which the legacy path never applied to a checkbox at all — so the tick
            // was merchant-coloured on one bundle and the browser's default on the other.
            checkbox: { accentColor: a.accent_color },
        },
        numberField: {
            container: { marginBottom: '10px' },
            // `.cod-stepper { display: inline-flex; align-items: stretch; overflow: hidden }` — the
            // FIRST port omitted the display and sizing, so the group stretched across the whole product
            // card with `−` and `+` pinned to opposite edges and the count floating between them.
            wrapper: {
                boxSizing: 'border-box',
                display: 'inline-flex',
                alignItems: 'stretch',
                // `fit-content` is what stops a flex parent stretching it. Without it the group fills.
                width: 'fit-content',
                flexShrink: 0,
                overflow: 'hidden',
                backgroundColor: pc.qc_bg_color,
                border: border(pc.qc_border_width, pc.qc_border_color),
                borderRadius: px(pc.qc_border_radius),
            },
            label: labelBase,
            // `.cod-qty-value { min-width: 40px; display: flex; align-items: center; justify-content: center }`
            input: {
                ...THEME_RESET,
                color: fs.text_color,
                minWidth: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '15px',
                borderLeft: border(pc.qc_border_width, pc.qc_border_color),
                borderRight: border(pc.qc_border_width, pc.qc_border_color),
            },
            // `.cod-qty-btn { width: 34px; height: 34px; border: none; background: transparent }`
            decrementButton: {
                ...THEME_RESET,
                boxSizing: 'border-box',
                width: '34px',
                height: '34px',
                padding: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: a.text_color,
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
            },
            incrementButton: {
                ...THEME_RESET,
                boxSizing: 'border-box',
                width: '34px',
                height: '34px',
                padding: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: a.text_color,
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
            },
        },
        productCard: {
            container: {
                // `.cod-product-card { display: flex; align-items: center; gap: 14px }`
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: pc.bg_color,
                border: border(pc.border_width, pc.border_color),
                borderRadius: px(pc.border_radius),
                // `.cod-product-card { padding: 14px; margin-bottom: 16px }`
                padding: '14px',
                marginBottom: '16px',
            },
            thumb: {
                // `.cod-thumb-wrap { width: 64px; height: 64px; flex: 0 0 auto }`
                boxSizing: 'border-box',
                width: '64px',
                height: '64px',
                objectFit: 'cover',
                border: '1px solid rgba(0,0,0,.12)',
                borderRadius: '8px',
                flexShrink: 0,
            },
            // `.cod-qty-badge` — the count that overlaps the thumbnail's corner.
            thumbWrap: { position: 'relative', flexShrink: 0, width: '64px', height: '64px' },
            badge: {
                ...THEME_RESET,
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                minWidth: '20px',
                height: '20px',
                padding: '0 5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#4a4a4a',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '999px',
                boxSizing: 'border-box',
                zIndex: '1',
                margin: '0',
            },
            name: { ...THEME_RESET, color: a.text_color, fontSize: '16px', fontWeight: '700' },
            // `.cod-product-variant-name { margin: 2px 0 0 }` / `.cod-product-price { margin: 2px 0 8px }`
            // — the spacing inside the card is these margins, NOT a flex `gap`. The first port used a
            // 4px gap on the column, which stacked on top of them and left the card visibly airier than
            // the Designer.
            variant: {
                ...THEME_RESET,
                color: a.text_color,
                fontSize: '13px',
                opacity: '0.6',
                marginTop: '2px',
            },
            price: {
                ...THEME_RESET,
                color: a.text_color,
                fontSize: '15px',
                marginTop: '2px',
                marginBottom: '8px',
            },
        },
        // `.cod-cart-items { padding: 6px 14px; margin-bottom: 16px }` — the SAME card chrome as the
        // single-product card, but a block box: the rows stack and each draws its own separator, so the
        // container must not be the centred flex row `.cod-product-card` is.
        cartList: {
            container: {
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: pc.bg_color,
                border: border(pc.border_width, pc.border_color),
                borderRadius: px(pc.border_radius),
                padding: '6px 14px',
                marginBottom: '16px',
            },
            // `.cod-cart-item { display:flex; align-items:center; justify-content:space-between;
            //  gap:12px; padding:8px 0; border-bottom:1px solid rgba(0,0,0,.08) }`
            item: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '8px 0',
                width: '100%',
                boxSizing: 'border-box',
                borderBottom: '1px solid rgba(0,0,0,.08)',
            },
            itemLast: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '8px 0',
                width: '100%',
                boxSizing: 'border-box',
                borderBottom: 'none',
            },
            // 44px and radius 6 — the cart thumbs are SMALLER than the single-product card's 64px.
            thumb: {
                boxSizing: 'border-box',
                width: '44px',
                height: '44px',
                flexShrink: 0,
                borderRadius: '6px',
                objectFit: 'cover',
                border: '1px solid rgba(0,0,0,.12)',
                backgroundColor: '#f1f1f1',
            },
            thumbEmpty: {
                boxSizing: 'border-box',
                width: '44px',
                height: '44px',
                flexShrink: 0,
                borderRadius: '6px',
                border: '1px solid rgba(0,0,0,.12)',
                backgroundColor: '#f1f1f1',
            },
            // `.cod-cart-item-text { flex: 1; min-width: 0 }`
            text: { display: 'flex', flexDirection: 'column', flexGrow: '1', minWidth: '0' },
            // 14px NORMAL weight — not the product card's 16px/700.
            name: { ...THEME_RESET, color: a.text_color, fontSize: '14px', overflow: 'hidden' },
            variant: { ...THEME_RESET, fontSize: '12px', color: 'rgba(0,0,0,.55)', overflow: 'hidden' },
            price: {
                ...THEME_RESET,
                color: a.text_color,
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0,
            },
            // A row of the list is `align-items: center`, so the stepper needs `align-self: flex-start`
            // to sit at the text column's left edge rather than being centred with it.
            stepper: { alignSelf: 'flex-start', marginTop: '6px' },
            // `.cod-cart-item-remove { padding:4px; font-size:20px; opacity:.45 }`
            remove: {
                ...THEME_RESET,
                flexShrink: 0,
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: '1',
                fontSize: '20px',
                color: a.text_color,
                opacity: '0.45',
            },
        },
        // `.cod-product-variants { display:flex; flex-wrap:wrap; gap:6px; margin:0 0 8px }`
        productVariants: { display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '0 0 8px' },
        // `.cod-product-variant { font-size:12px; line-height:1.2; padding:3px 8px;
        //  border-radius:999px; background:rgba(0,0,0,.06); color:#444 }`
        productVariant: {
            ...THEME_RESET,
            fontSize: '12px',
            lineHeight: '1.2',
            padding: '3px 8px',
            borderRadius: '999px',
            backgroundColor: 'rgba(0,0,0,.06)',
            color: '#444444',
        },
        // `.cod-product-compare { color: rgba(0,0,0,.5); text-decoration: line-through }`
        productCompare: {
            ...THEME_RESET,
            color: 'rgba(0,0,0,.5)',
            textDecoration: 'line-through !important',
        },
        productNet: { ...THEME_RESET, color: a.text_color, fontWeight: '600' },
        totals: {
            container: {
                ...THEME_RESET,
                backgroundColor: ts.bg_color,
                color: ts.text_color,
                border: border(ts.border_width, ts.border_color),
                borderRadius: px(ts.border_radius),
                // `.cod-summary { padding: 16px; margin-bottom: 18px; font-size: 15px }`
                padding: '16px',
                // Re-declared AFTER the reset, which sets the `margin` shorthand — see its note.
                marginBottom: '18px',
                fontSize: '15px',
                width: '100%',
                boxSizing: 'border-box',
            },
            // `width: 100%` is load-bearing: without it the row shrinks to its content and
            // `justify-content: space-between` has no free space, so label and amount render touching
            // ("Subtotal10,000,000.00 MXN" on a live store).
            // 15px, not 14px: `.cod-summary { font-size: 15px }` and the rows inherit it. The first port
            // sized them at 14px, so every amount in the box read a point small against the Designer.
            row: { ...THEME_RESET, fontSize: '15px', width: '100%', marginBottom: '8px' },
            // `.cod-summary-divider { margin: 12px 0 }`
            divider: {
                boxSizing: 'border-box',
                borderTop: ts.divider_width > 0 ? `${px(ts.divider_width)} solid ${ts.divider_color}` : 'none',
                margin: '12px 0',
                width: '100%',
            },
            totalRow: {
                ...THEME_RESET,
                fontSize: '18px',
                fontWeight: '700',
                width: '100%',
                marginBottom: '0',
            },
            // `.cod-total-amount { font-weight: 800 }` — the amount is heavier than its own label.
            totalAmount: { ...THEME_RESET, fontWeight: '800' },
        },
        discount: {
            container: { margin: '0 0 14px', width: '100%' },
            // `flex: 1` with `minWidth: 0` — `flexGrow` alone left the input at its content width with a
            // gap before the Apply button, because a flex item will not shrink below its content
            // without an explicit min-width override.
            // `.cod-discount .cod-input { flex: 1 }` — the input takes ALL the leftover width and the
            // Apply button keeps its content width. On a live store the pair rendered the other way
            // round: a stub of an input beside an oversized button. The `flex` shorthand alone did not
            // survive, so the three longhands and `width: 100%` are stated together — whichever the host
            // honours produces the same result.
            inputWrap: {
                position: 'relative',
                display: 'flex',
                alignItems: 'stretch',
                // `alignSelf`, not just the row's `align-items`: on a live store the Apply button set the
                // row's height and the input stayed short, so the pair did not line up the way the
                // Designer shows them.
                alignSelf: 'stretch',
                flexGrow: '1',
                flexShrink: '1',
                flexBasis: '0%',
                width: '100%',
                minWidth: '0',
            },
            input: {
                backgroundColor: ds.input_bg_color,
                color: ds.input_text_color,
                border: border(ds.input_border_width, ds.input_border_color),
                borderRadius: px(ds.input_border_radius),
                padding: `${fieldPadY} 12px`,
                // `.cod-discount .cod-input` inherits `.cod-input`'s 15px — the discount block overrides
                // only colours, border and radius.
                fontSize: '15px',
                width: '100%',
                // The same pinned height as the Apply button. `align-items: stretch` was supposed to do
                // this and did not: the button's own height won and the input stayed short beside it.
                height: discountControlHeight(config),
                lineHeight: '20px',
                boxSizing: 'border-box',
            },
            applyButton: {
                ...THEME_RESET,
                backgroundColor: ds.button_bg_color,
                color: ds.button_text_color,
                border: border(ds.button_border_width, ds.button_border_color),
                borderRadius: px(ds.button_border_radius),
                // 🚨 `!important`, for the same reason THEME_RESET needs it: storefront themes write their
                // button padding as `!important`, and an `!important` author rule beats a plain inline
                // style. Without it the theme's padding won and the Apply button rendered half again as
                // tall as the input beside it — `0 14px` alone changed nothing on a live store.
                //
                // Vertical padding is zero by design: the row stretches the button to the input's height,
                // so any vertical padding only adds to a height that is already decided.
                padding: '0 14px',
                // Pinned to the SAME height as the input beside it — see `discountControlHeight`. Stated
                // in both channels: here so the shape is right if `styled()` is ever ignored, and in
                // `discountApplyCss` so it survives a theme that sizes its buttons with `!important`.
                height: discountControlHeight(config),
                boxSizing: 'border-box',
                lineHeight: '1',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                // Never shrink and never wrap — the button is the fixed side of the pair.
                flexShrink: 0,
                alignSelf: 'stretch',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
            // `.cod-discount-feedback { font-size: 13px; margin-top: 6px }` with
            // `.cod-discount-ok { color: #1b8a3f }` / `.cod-discount-error { color: #c0392b }`.
            appliedLine: { ...THEME_RESET, color: '#1b8a3f', fontSize: '13px', marginTop: '6px' },
            errorLine: { ...THEME_RESET, color: '#c0392b', fontSize: '13px', marginTop: '6px' },
        },
        shippingMethod: {
            container: { marginBottom: '18px', width: '100%' },
            options: { display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' },
            // `.cod-radio { width:18px; height:18px; border-radius:999px; border:2px solid accent }`
            // NubeSDK has no radio component, so the marker is a styled box — a square checkbox next to
            // a round one in the Designer is the kind of mismatch the merchant notices immediately.
            radio: {
                width: '18px',
                height: '18px',
                borderRadius: '999px',
                border: `2px solid ${a.accent_color}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxSizing: 'border-box',
            },
            radioOn: {
                width: '18px',
                height: '18px',
                borderRadius: '999px',
                border: `2px solid ${a.accent_color}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxSizing: 'border-box',
            },
            // `.cod-radio.cod-radio-on::after { width: 9px; height: 9px; border-radius: 999px }`
            radioDot: {
                width: '9px',
                height: '9px',
                borderRadius: '999px',
                backgroundColor: a.accent_color,
            },
            title: {
                ...THEME_RESET,
                color: a.text_color,
                fontSize: '17px',
                fontWeight: '600',
                marginBottom: '8px',
            },
            // `.cod-shipping-card { display:flex; align-items:center; justify-content:space-between;
            //  gap:12px; padding:14px 16px; cursor:pointer }` — and NO margin: the 8px between cards is
            // the `gap` on `.cod-shipping-options`. The first port carried both a gap and a 6px margin,
            // which is the extra air the merchant spotted between the rate cards.
            option: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                backgroundColor: sm.bg_color,
                color: sm.text_color,
                border: border(sm.border_width, sm.border_color),
                borderRadius: px(sm.border_radius),
                padding: '14px 16px',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'left',
                cursor: 'pointer',
            },
            // Selection is a border-colour swap, so the card cannot shift by a pixel when picked —
            // width stays identical between the two objects.
            optionSelected: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                backgroundColor: sm.bg_color,
                color: sm.text_color,
                border: border(Math.max(sm.border_width, 1), sm.active_border_color),
                borderRadius: px(sm.border_radius),
                padding: '14px 16px',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'left',
                cursor: 'pointer',
            },
            // The rate price wrapped onto two lines ("1000.00 / MXN") because the flex item was allowed
            // to shrink below its content width.
            // 🚨 The price and description are SECONDARY text, and that was originally expressed by
            // baking the de-emphasis into a black colour (`rgba(0,0,0,.6)`). That only works while the
            // text is black: set `text_color` to white for a dark card and the price rendered black on
            // black — invisible, with no setting that could fix it.
            //
            // `opacity` instead of a derived `rgba()` because `cssColor` is any CSS colour string —
            // `#fff`, `rgb()`, `hsl()`, a named colour — so there is nothing safe to parse. Opacity keeps
            // the hierarchy in whatever colour the merchant chose.
            optionPrice: {
                ...THEME_RESET,
                color: sm.text_color,
                opacity: 0.6,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                marginLeft: '12px',
            },
            // `.cod-shipping-name { font-weight: 700 }` and nothing else — so it inherits the form's
            // 16px, not 14px. THEME_RESET is what keeps a theme from uppercasing it, which is how
            // "Free shipping" reached a live storefront as "FREE SHIPPING".
            optionName: { ...THEME_RESET, color: sm.text_color, fontWeight: '700' },
            // Same fix as `optionPrice` — it had the identical hardcoded black.
            optionDesc: { ...THEME_RESET, fontSize: '12px', color: sm.text_color, opacity: 0.55 },
        },
        submit: {
            ...THEME_RESET,
            boxSizing: 'border-box',
            // Same flex layout as the open button — the submit label can carry a subtitle beneath it.
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            backgroundColor: config.submit.style.bg_color,
            color: config.submit.style.text_color,
            // A null radius means "inherit the form's global radius" — the merchant has not overridden it.
            borderRadius: px(config.submit.style.border_radius ?? a.border_radius),
            fontSize: px(config.submit.style.font_size),
            // 🚨 The Designer offers a border width/colour and a shadow for the submit button
            // (`defaultSubmitStyle`), and the legacy set both inline (`form.ts:957-960`). The first port
            // omitted them entirely, so a merchant who configured a border saw it in the preview and not
            // on the storefront.
            border: border(config.submit.style.border_width, config.submit.style.border_color),
            ...(config.submit.style.shadow > 0
                ? {
                    boxShadow: `0 ${px(Math.round(config.submit.style.shadow / 2))} ${px(config.submit.style.shadow)} rgba(0,0,0,.25)`,
                }
                : {}),
            padding: '16px',
            margin: '6px 0 14px',
            width: '100%',
            fontWeight: '700',
            cursor: 'pointer',
            // ⚠️ AFTER everything, and deliberately. `.cod-submit` sets `text-transform: uppercase;
            // letter-spacing: .5px` — the submit button is MEANT to shout, and the Designer shows
            // "FINALIZAR COMPRA" in caps. THEME_RESET's `none !important` was overriding the design,
            // not the theme. Order matters here for exactly the reason documented on THEME_RESET.
            textTransform: 'uppercase !important',
            letterSpacing: '.5px !important',
        },
        // `.cod-action-subtitle { font-size: .8em; font-weight: 500; opacity: .9; text-transform: none;
        //  letter-spacing: normal }` — the subtitle stays sentence-case under an uppercase label.
        submitSubtitle: {
            ...THEME_RESET,
            fontSize: '.8em',
            fontWeight: '500',
            opacity: '.9',
            textTransform: 'none !important',
            letterSpacing: 'normal !important',
            margin: '0',
        },
        terms: {
            label: {
                ...THEME_RESET,
                color: config.consent.terms.style.text_color,
                fontSize: px(config.consent.terms.style.font_size),
            },
            // The reset without `textDecoration` — a link that does not look like one is not a link.
            link: {
                ...THEME_RESET,
                textDecoration: 'underline',
                color: config.consent.terms.style.link_color,
            },
        },
        newsletter: {
            label: {
                ...THEME_RESET,
                color: config.consent.newsletter.style.text_color,
                fontSize: px(config.consent.newsletter.style.font_size),
            },
        },
        // `.cod-success-panel { text-align: center; padding: 30px 8px }`
        successPanel: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '30px 8px',
            width: '100%',
            boxSizing: 'border-box',
            gap: '8px',
        },
        // `.cod-success-panel .cod-check` sets only a size, so the legacy ✔ takes the form's text
        // colour. Stated explicitly here rather than left as `primary_color`, which no legacy buyer
        // ever saw on it.
        successCheck: { ...THEME_RESET, fontSize: '40px', color: a.text_color },
        /**
         * The wrapper the merchant's own thank-you HTML renders inside.
         *
         * 🚨 **`successPanel` centres, and a merchant's page is not centred prose.** It sets
         * `text-align: center` (inherited, so it reached every line of the page) and
         * `align-items: center` (so the page sized to its content instead of the panel, which is what
         * let an over-wide design spill off both edges rather than one). The legacy bundle has said
         * the same thing since it shipped — `.cod-thankyou-page { text-align: left }` is declared after
         * `.cod-success-panel` precisely to undo it — so this is parity, not a new opinion.
         *
         * `width: 100%` is the other half: it makes the page fill the panel under `align-items: center`,
         * which is where `htmlTree`'s percentage widths finally have something definite to resolve
         * against.
         */
        thankYouPage: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            minWidth: '0',
            boxSizing: 'border-box',
            textAlign: 'left',
            lineHeight: '1.5',
        },
        message: {
            error: {
                ...THEME_RESET,
                color: '#b42318',
                backgroundColor: '#fee4e2',
                padding: '10px 12px',
                borderRadius: '8px',
            },
            success: {
                ...THEME_RESET,
                color: '#027a48',
                backgroundColor: '#d1fadf',
                padding: '10px 12px',
                borderRadius: '8px',
            },
        },
    };
}
//# sourceMappingURL=theme.js.map